import { useCallback } from 'react';
import { Pencil, TrashIcon } from 'assets/icons';
import Button from 'components/button';
import { cardIconButtonClass } from 'components/card/PanelCard';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';
import {
  COMMENT_ACTIONS_REVEAL_CLASS,
  COMMENT_ROW_CLASS,
} from 'components/comments/commentActionsClass';
import {
  COMMENT_COUNTER_FROM,
  COMMENT_MAX_LENGTH,
  authorInitialsOf,
  authorNameOf,
  commentLengthOf,
  deleteErrorText,
  editErrorText,
  formatCommentAt,
  isCommentSendable,
  sendErrorText,
} from './invoiceCommentText';

const TEXTAREA_CLASS_NAME =
  'w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-yellow-500';

// Shown from COMMENT_COUNTER_FROM on, so that the limit is seen before it bites.
const LengthCounter = ({ draft, text }) => {
  const length = commentLengthOf(draft);
  if (length < COMMENT_COUNTER_FROM) return null;
  const isTooLong = length > COMMENT_MAX_LENGTH;
  return (
    <div
      aria-live="polite"
      className={`text-xs text-right ${isTooLong ? 'text-red-600' : 'text-gray-500'}`}
    >
      {fillText(text('counter', '{count} / {max}'), {
        count: length,
        max: COMMENT_MAX_LENGTH,
      })}
      {isTooLong && (
        <span className="ml-2">
          {text('tooLong', 'Un commentaire fait au plus 2 000 caractères.')}
        </span>
      )}
    </div>
  );
};

// The edit stays in the row: the Admin shows one modal at a time, and the
// panel is already under it.
const CommentEditor = ({ comments, text }) => {
  const { editing, changeEditDraft, saveEdit, cancelEdit } = comments;
  const { isSavingEdit, editFailure } = comments;
  const canSave =
    !isSavingEdit &&
    isCommentSendable(editing.draft) &&
    editing.draft.trim() !== editing.original;
  return (
    <div className="mt-2 flex flex-col gap-2">
      <textarea
        aria-label={text('editInputLabel', 'Modifier le commentaire')}
        value={editing.draft}
        readOnly={isSavingEdit}
        rows={3}
        className={TEXTAREA_CLASS_NAME}
        onChange={(event) => changeEditDraft(event.target.value)}
      />
      <LengthCounter draft={editing.draft} text={text} />
      {editFailure && (
        <div role="alert" className="text-sm text-red-600">
          {editErrorText(text, editFailure)}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          size="SM"
          color={isSavingEdit ? 'DISABLED' : 'SECONDARY'}
          disabled={isSavingEdit}
          onClick={cancelEdit}
        >
          {text('editCancel', 'Annuler')}
        </Button>
        <Button
          size="SM"
          color={canSave ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSave}
          aria-busy={isSavingEdit || undefined}
          onClick={saveEdit}
        >
          {text('editSubmit', 'Enregistrer')}
        </Button>
      </div>
    </div>
  );
};

const CommentRow = ({ comment, comments, text }) => {
  // Strictly true: the api decides who may edit or delete, an absent flag hides it.
  const canEdit = comment.canEdit === true;
  const canDelete = comment.canDelete === true;
  const isEditing = comments.editing?.id === comment.id;
  const isDeleting = comments.deletingIds.includes(comment.id);
  const deleteFailure = comments.deleteFailures[comment.id];
  const editLabel = text('editLabel', 'Modifier le commentaire');
  const deleteLabel = text('deleteLabel', 'Supprimer le commentaire');
  // A pending or failed delete keeps its buttons in view, next to its state.
  const keepActionsVisible = isDeleting || Boolean(deleteFailure);
  return (
    <li
      className={`${COMMENT_ROW_CLASS} flex gap-3 rounded-lg border border-gray-100 px-3 py-3`}
    >
      <div
        aria-hidden
        className="flex flex-shrink-0 items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-xs font-medium text-gray-700"
      >
        {authorInitialsOf(comment.author)}
      </div>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 text-sm">
            <span className="font-medium text-gray-900">
              {authorNameOf(text, comment.author)}
            </span>
            <span className="ml-2 text-gray-500">
              {formatCommentAt(text, comment.createdAt)}
            </span>
            {comment.editedAt && (
              <span className="ml-1 text-gray-500">
                · {text('edited', 'modifié')}
              </span>
            )}
          </div>
          <div
            data-testid="comment-actions"
            className={`flex items-start gap-2 ${keepActionsVisible ? '' : COMMENT_ACTIONS_REVEAL_CLASS}`}
          >
            {canEdit && !isEditing && (
              <Tooltip text={editLabel} placement="top">
                <button
                  type="button"
                  className={cardIconButtonClass}
                  aria-label={editLabel}
                  disabled={isDeleting}
                  onClick={() => comments.startEdit(comment)}
                >
                  <Pencil wh={18} color="#374151" />
                </button>
              </Tooltip>
            )}
            {canDelete && !isEditing && (
              <Tooltip text={deleteLabel} placement="top">
                <button
                  type="button"
                  className={`${cardIconButtonClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                  aria-label={deleteLabel}
                  aria-busy={isDeleting || undefined}
                  disabled={isDeleting}
                  onClick={() => comments.deleteComment(comment.id)}
                >
                  <TrashIcon wh={18} color="#374151" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        {isEditing ? (
          <CommentEditor comments={comments} text={text} />
        ) : (
          /* A text node, never markup: React escapes it, pre-wrap keeps its line breaks. */
          <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
            {comment.text}
          </p>
        )}
        {deleteFailure && (
          <div role="alert" className="mt-2 text-sm text-red-600">
            {deleteErrorText(text, deleteFailure)}
          </div>
        )}
      </div>
    </li>
  );
};

const CommentForm = ({ comments, text }) => {
  const { draft, changeDraft, send, isSending, sendFailure } = comments;
  const canSend = isCommentSendable(draft) && !isSending;
  return (
    <div className="flex flex-col gap-2">
      <textarea
        aria-label={text('inputLabel', 'Votre commentaire')}
        placeholder={text('placeholder', 'Écrire un commentaire…')}
        value={draft}
        readOnly={isSending}
        rows={3}
        className={TEXTAREA_CLASS_NAME}
        onChange={(event) => changeDraft(event.target.value)}
      />
      <LengthCounter draft={draft} text={text} />
      {sendFailure && (
        <div role="alert" className="text-sm text-red-600">
          {sendErrorText(text, sendFailure)}
        </div>
      )}
      <div className="flex justify-end">
        <Button
          color={canSend ? 'PRIMARY' : 'DISABLED'}
          disabled={!canSend}
          aria-busy={isSending || undefined}
          onClick={send}
        >
          {text('submit', 'Commenter')}
        </Button>
      </div>
    </div>
  );
};

/**
 * The "Commentaires" tab of an invoice's detail (JBWEB-1279): notes the admins
 * write to each other, oldest first. They live in Jobbiz only (api decision
 * 58); their author edits one (api decision 91), their author or a
 * super-admin deletes one in a click (JBWEB-1389), which the history records
 * without its text. `comments` comes from `useInvoiceComments`, held by the
 * panel.
 */
const InvoiceCommentsTab = ({ comments }) => {
  const { language } = UseContext();
  const text = useCallback(
    (key, fallback) => language.getValue(`invoiceComments.${key}`, fallback),
    [language],
  );

  if (comments.failure) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-sm text-gray-700"
      >
        <div>
          {comments.failure === 'notFound'
            ? text(
                'notFound',
                'Cette facture est introuvable : elle n’a pas de commentaires.',
              )
            : text('loadFailed', 'Les commentaires n’ont pas pu être chargés.')}
        </div>
        {comments.failure === 'failed' && (
          <Button color="SECONDARY" onClick={comments.reload}>
            {text('retryLoad', 'Réessayer')}
          </Button>
        )}
      </div>
    );
  }
  if (!comments.items) {
    return (
      <div role="status" className="py-16 text-center text-sm text-gray-500">
        {text('loading', 'Chargement des commentaires…')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {comments.items.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-500">
          {text('empty', 'Aucun commentaire')}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {comments.items.map((comment) => (
            <CommentRow
              key={comment.id}
              comment={comment}
              comments={comments}
              text={text}
            />
          ))}
        </ul>
      )}
      <CommentForm comments={comments} text={text} />
    </div>
  );
};

export default InvoiceCommentsTab;
